import bubble1 from '~/assets/bubbles/bubble-1.webp'
import bubble2 from '~/assets/bubbles/bubble-2.webp'
import bubble3 from '~/assets/bubbles/bubble-3.webp'
import bubble4 from '~/assets/bubbles/bubble-4.webp'
import bubble5 from '~/assets/bubbles/bubble-5.webp'
import bubble6 from '~/assets/bubbles/bubble-6.webp'
import bubble7 from '~/assets/bubbles/bubble-7.webp'
import bubble8 from '~/assets/bubbles/bubble-8.webp'
import bubble9 from '~/assets/bubbles/bubble-9.webp'
import bubble10 from '~/assets/bubbles/bubble-10.webp'

/**
 * Ten plates for the gigs and the acts that have no picture, which is 390 gigs and five acts
 * in six.
 *
 * They are not portraits and are not pretending to be. The thing they replace is the words
 * "no picture" in a bordered square — honest, and a page of them reads as a page of things
 * that are missing rather than a calendar. Initials were tried in that slot and rejected:
 * two letters in a box is a portrait that is wrong rather than a plate that is decorative.
 *
 * Imported one by one rather than through `import.meta.glob`, so a file that goes missing is
 * a build error and not an empty array at runtime — and so the bundler can hash them.
 */
const BUBBLES = [
  bubble1,
  bubble2,
  bubble3,
  bubble4,
  bubble5,
  bubble6,
  bubble7,
  bubble8,
  bubble9,
  bubble10,
] as const

/**
 * FNV-1a, 32-bit. Small, well-spread, and — the part that matters here — the same answer in
 * this browser tomorrow as in that one today.
 *
 * Random would be wrong twice over: a gig would change its face on every render, and the
 * same gig would wear two different faces in the grid and in its own sheet.
 *
 * The multiply is not covered by a test and cannot be: ten buckets cannot tell a mixed hash
 * from an unmixed one — "abc" and "cba" land on the same plate either way. It is here
 * because FNV-1a is FNV-1a, not because a test would notice if it went.
 */
function hash(seed: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/** The plate for a thing without a picture. Same seed, same plate, always. */
export function bubbleFor(seed: string): string {
  const at = hash(seed) % BUBBLES.length
  // `noUncheckedIndexedAccess`: the modulo cannot leave the array, but the compiler has no
  // way to know that, and asserting it away would be a lie the next edit could make true.
  return BUBBLES[at] ?? BUBBLES[0]
}
